import { useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router";
import { ArrowLeft, Monitor, Lock, MessageSquare, Bug } from "lucide-react";
import { useAuthStore } from "../../stores/authStore";
import { Badge } from "../../components/shared/Badge";
import { Avatar } from "../../components/shared/Avatar";
import { cn } from "@nthamizh/ui";
import { useToast } from "@nthamizh/ui";
import {
  useUpdateProfile, useChangePassword,
  useSessions, useRevokeSession, useRevokeAllOtherSessions,
  useMyFeedback, useMyStorage,
} from "@platform/hooks";

// ── Shared sub-page shell ─────────────────────────────────────────────────────

function SubPageShell({ title, children }: { title: string; children: React.ReactNode }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-5 max-w-lg">
      <button
        onClick={() => navigate("/profile")}
        className="flex items-center gap-1.5 text-sm text-ds-text-secondary hover:text-ds-text transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back to profile
      </button>
      <h1 className="text-lg font-semibold text-ds-text">{title}</h1>
      {children}
    </div>
  );
}

// ── Layout — wraps both the main profile page AND sub-pages ───────────────────

export function ProfileLayout() {
  const location = useLocation();
  const isSubPage = location.pathname !== "/profile";

  if (isSubPage) {
    return (
      <div className="max-w-2xl">
        <Outlet />
      </div>
    );
  }

  return <ProfilePage />;
}

// ── Toggle row ────────────────────────────────────────────────────────────────

function ToggleRow({ label, description, checked, onChange }: {
  label: string; description: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between py-3.5 border-b border-ds-border last:border-0">
      <div>
        <p className="text-sm font-medium text-ds-text">{label}</p>
        <p className="text-xs text-ds-text-muted mt-0.5">{description}</p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ml-4",
          checked ? "bg-indigo-600" : "bg-gray-200"
        )}
      >
        <span className={cn(
          "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform",
          checked ? "translate-x-4" : "translate-x-0.5"
        )} />
      </button>
    </div>
  );
}

// ── Main profile page ─────────────────────────────────────────────────────────

function ProfilePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [activeTab, setActiveTab] = useState<"account" | "notifications">("account");

  // Personal info state
  const updateProfile = useUpdateProfile();
  const { data: storageStats } = useMyStorage();
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: user?.name ?? "",
    jobTitle: user?.jobTitle ?? "",
    timezone: user?.timezone ?? "UTC",
    dateFormat: user?.dateFormat ?? "DD/MM/YYYY",
  });

  const handleSave = async () => {
    try {
      await updateProfile.mutateAsync(form);
      toast({ title: "Profile saved", variant: "success" });
    } catch {
      toast({ title: "Failed to save profile", variant: "error" });
    }
  };

  // Notifications state
  const [notifs, setNotifs] = useState({
    migration: true, approvals: true, roles: true,
    digest: false, syncAlerts: true, badge: true,
  });

  const roles = user?.roles ?? [];

  return (
    <div className="space-y-0 max-w-2xl">
      {/* Hero */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-4">
          <Avatar name={user?.name ?? "?"} size="lg" />
          <div>
            <p className="text-base font-semibold text-ds-text">{user?.name}</p>
            <p className="text-sm text-ds-text-secondary">{user?.email}</p>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {user?.platformRole && (
                <Badge variant="purple">{user.platformRole.replace("_", " ")}</Badge>
              )}
              <Badge variant="green">Active</Badge>
            </div>
          </div>
        </div>
        {/* Storage bar — hidden for platform admins (no limit) */}
        {storageStats && !storageStats.isPlatformAdmin && (
          <div className="mt-4 p-3 bg-ds-bg border border-ds-border rounded-xl">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-medium text-ds-text-secondary">Storage</p>
              <p className="text-xs text-ds-text-muted">
                {(storageStats.usedBytes / (1024 * 1024)).toFixed(1)} MB / {(storageStats.quotaBytes / (1024 * 1024)).toFixed(0)} MB
              </p>
            </div>
            <div className="h-1.5 bg-white border border-ds-border rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${(storageStats.usedBytes / storageStats.quotaBytes) >= 0.9 ? "bg-red-500" : "bg-indigo-500"}`}
                style={{ width: `${Math.min(100, (storageStats.usedBytes / storageStats.quotaBytes) * 100).toFixed(1)}%` }}
              />
            </div>
          </div>
        )}

        {/* Action buttons — open sub-pages */}
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={() => navigate("/profile/password")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-ds-border text-sm text-ds-text-secondary hover:bg-ds-bg transition-colors"
          >
            <Lock className="w-3.5 h-3.5" /> Reset password
          </button>
          <button
            onClick={() => navigate("/profile/sessions")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-ds-border text-sm text-ds-text-secondary hover:bg-ds-bg transition-colors"
          >
            <Monitor className="w-3.5 h-3.5" /> Sessions
          </button>
          <button
            onClick={() => navigate("/profile/feedback")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-ds-border text-sm text-ds-text-secondary hover:bg-ds-bg transition-colors"
          >
            <MessageSquare className="w-3.5 h-3.5" /> My feedback
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-ds-border mb-5">
        {(["account", "notifications"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              "px-5 py-2.5 text-sm font-medium border-b-2 transition-colors capitalize",
              activeTab === tab
                ? "border-indigo-600 text-indigo-700"
                : "border-transparent text-ds-text-secondary hover:text-ds-text"
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Account tab */}
      {activeTab === "account" && (
        <div className="space-y-4">
          {/* Personal info */}
          <div className="bg-white border border-ds-border rounded-card shadow-card p-5">
            <p className="text-xs font-semibold text-ds-text-muted uppercase tracking-wide mb-4">Personal info</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Full name</label>
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Email address</label>
                <input defaultValue={user?.email} disabled
                  className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg bg-ds-bg opacity-60 cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">
                  Job title <span className="text-ds-text-muted font-normal">(optional)</span>
                </label>
                <input value={form.jobTitle} onChange={(e) => setForm((f) => ({ ...f, jobTitle: e.target.value }))}
                  placeholder="Oracle Functional Consultant"
                  className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Timezone</label>
                  <select value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 bg-white">
                    <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                    <option value="Europe/London">Europe/London (GMT)</option>
                    <option value="America/New_York">America/New_York (EST)</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Date format</label>
                  <select value={form.dateFormat} onChange={(e) => setForm((f) => ({ ...f, dateFormat: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 bg-white">
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={handleSave} disabled={updateProfile.isPending}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                {updateProfile.isPending ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>

          {/* Enterprises */}
          <div className="bg-white border border-ds-border rounded-card shadow-card p-5">
            <p className="text-xs font-semibold text-ds-text-muted uppercase tracking-wide mb-4">Enterprises</p>
            {(!user?.roles || user.roles.length === 0) ? (
              <p className="text-sm text-ds-text-muted">No enterprise memberships found.</p>
            ) : (
              <div className="divide-y divide-ds-border">
                <div className="flex items-center gap-3 py-2.5">
                  <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0 text-white text-xs font-semibold">
                    {window.location.hostname.split(".")[0]?.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ds-text capitalize">
                      {window.location.hostname.split(".")[0]}
                    </p>
                    <p className="text-xs text-ds-text-muted">{window.location.hostname}</p>
                  </div>
                  <Badge variant="green">Default</Badge>
                </div>
              </div>
            )}
            <p className="text-xs text-ds-text-muted mt-3">
              Your default enterprise is managed by your platform administrator.
            </p>
          </div>

          {/* My roles */}
          <div className="bg-white border border-ds-border rounded-card shadow-card p-5">
            <p className="text-xs font-semibold text-ds-text-muted uppercase tracking-wide mb-4">My roles</p>
            {roles.length === 0 ? (
              <p className="text-sm text-ds-text-muted">No roles assigned yet.</p>
            ) : (
              <div className="divide-y divide-ds-border">
                {roles.map((r) => (
                  <div key={r.roleId} className="flex items-center justify-between py-2.5">
                    <div>
                      <p className="text-sm font-medium text-ds-text">{r.roleName}</p>
                      {r.moduleKey && (
                        <p className="text-xs text-ds-text-muted capitalize">{r.moduleKey} module</p>
                      )}
                    </div>
                    <Badge variant="blue">Active</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notifications tab */}
      {activeTab === "notifications" && (
        <div className="space-y-4">
          <div className="bg-white border border-ds-border rounded-card shadow-card p-5">
            <p className="text-xs font-semibold text-ds-text-muted uppercase tracking-wide mb-3">Email</p>
            <ToggleRow label="Migration activity" description="When Fast Formula files are synced or merged."
              checked={notifs.migration} onChange={(v) => setNotifs((n) => ({ ...n, migration: v }))} />
            <ToggleRow label="User approvals" description="When a new user requests access."
              checked={notifs.approvals} onChange={(v) => setNotifs((n) => ({ ...n, approvals: v }))} />
            <ToggleRow label="Role changes" description="When your roles or permissions are updated."
              checked={notifs.roles} onChange={(v) => setNotifs((n) => ({ ...n, roles: v }))} />
            <ToggleRow label="Weekly digest" description="Summary of enterprise activity each Monday."
              checked={notifs.digest} onChange={(v) => setNotifs((n) => ({ ...n, digest: v }))} />
          </div>
          <div className="bg-white border border-ds-border rounded-card shadow-card p-5">
            <p className="text-xs font-semibold text-ds-text-muted uppercase tracking-wide mb-3">In-app</p>
            <ToggleRow label="Sync status alerts" description="Show banner when GitHub sync fails."
              checked={notifs.syncAlerts} onChange={(v) => setNotifs((n) => ({ ...n, syncAlerts: v }))} />
            <ToggleRow label="Pending approvals badge" description="Show count badge when approvals are waiting."
              checked={notifs.badge} onChange={(v) => setNotifs((n) => ({ ...n, badge: v }))} />
          </div>
          <div className="flex justify-end">
            <button onClick={() => toast({ title: "Notification preferences saved", variant: "success" })}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">
              Save preferences
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Password sub-page ─────────────────────────────────────────────────────────

export function PasswordPage() {
  const changePassword = useChangePassword();
  const { toast } = useToast();
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [error, setError] = useState("");

  const score = [
    form.newPassword.length >= 8,
    form.newPassword.length >= 12,
    /[A-Z]/.test(form.newPassword) && /[0-9]/.test(form.newPassword),
    /[^A-Za-z0-9]/.test(form.newPassword),
  ].filter(Boolean).length;

  const colors = ["bg-red-400", "bg-amber-400", "bg-blue-400", "bg-emerald-500"];
  const labels = ["Weak", "Fair", "Good", "Strong"];

  const handleSubmit = async () => {
    setError("");
    if (form.newPassword !== form.confirmPassword) { setError("Passwords do not match."); return; }
    if (score < 2) { setError("Password is too weak."); return; }
    try {
      await changePassword.mutateAsync({ currentPassword: form.currentPassword, newPassword: form.newPassword });
      setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      toast({ title: "Password changed. All other sessions signed out.", variant: "success" });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to change password");
    }
  };

  return (
    <SubPageShell title="Reset password">
      <div className="bg-white border border-ds-border rounded-card shadow-card p-5 space-y-4">
        <div>
          <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Current password</label>
          <input type="password" value={form.currentPassword}
            onChange={(e) => setForm((f) => ({ ...f, currentPassword: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">New password</label>
          <input type="password" value={form.newPassword}
            onChange={(e) => setForm((f) => ({ ...f, newPassword: e.target.value }))}
            placeholder="At least 12 characters"
            className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400" />
          {form.newPassword.length > 0 && (
            <div className="mt-2">
              <div className="flex gap-1">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className={cn("flex-1 h-1 rounded-full transition-colors",
                    i < score ? colors[score - 1] : "bg-gray-200")} />
                ))}
              </div>
              <p className={cn("text-xs mt-1",
                score > 0 ? colors[score - 1]!.replace("bg-", "text-") : "text-gray-400")}>
                {labels[score - 1] ?? "Enter a password"}
              </p>
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Confirm new password</label>
          <input type="password" value={form.confirmPassword}
            onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400" />
        </div>
        {error && <p className="text-xs text-red-500">{error}</p>}
        <p className="text-xs text-ds-text-muted">
          Minimum 12 characters, at least one uppercase, one number, and one special character.
        </p>
        <div className="flex justify-end">
          <button onClick={handleSubmit}
            disabled={changePassword.isPending || !form.currentPassword || !form.newPassword}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            {changePassword.isPending ? "Updating..." : "Update password"}
          </button>
        </div>
      </div>
    </SubPageShell>
  );
}

// ── Sessions sub-page ─────────────────────────────────────────────────────────

interface Session { id: string; createdAt: string; expiresAt: string }

export function SessionsPage() {
  const { data: sessions = [], isLoading } = useSessions();
  const revokeSession = useRevokeSession();
  const revokeAll = useRevokeAllOtherSessions();
  const { toast } = useToast();
  const typedSessions = sessions as Session[];

  const handleRevoke = async (id: string) => {
    try { await revokeSession.mutateAsync(id); toast({ title: "Session revoked", variant: "success" }); }
    catch { toast({ title: "Failed to revoke session", variant: "error" }); }
  };

  const handleRevokeAll = async () => {
    try { await revokeAll.mutateAsync(""); toast({ title: "All other sessions signed out", variant: "success" }); }
    catch { toast({ title: "Failed to revoke sessions", variant: "error" }); }
  };

  return (
    <SubPageShell title="Active sessions">
      <div className="bg-white border border-ds-border rounded-card shadow-card overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-center text-sm text-ds-text-muted">Loading sessions...</div>
        ) : typedSessions.length === 0 ? (
          <div className="p-6 text-center text-sm text-ds-text-muted">No active sessions found.</div>
        ) : (
          <div className="divide-y divide-ds-border">
            {typedSessions.map((s, i) => (
              <div key={s.id} className="flex items-center gap-3 px-5 py-3.5">
                <div className="w-9 h-9 rounded-lg bg-ds-bg border border-ds-border flex items-center justify-center flex-shrink-0">
                  <Monitor className="w-4 h-4 text-ds-text-secondary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ds-text">Session {i + 1}</p>
                  <p className="text-xs text-ds-text-muted">
                    Created {new Date(s.createdAt).toLocaleDateString()} · Expires {new Date(s.expiresAt).toLocaleDateString()}
                  </p>
                </div>
                {i === 0 ? (
                  <span className="text-xs text-emerald-600 font-medium flex-shrink-0">Current</span>
                ) : (
                  <button onClick={() => handleRevoke(s.id)} disabled={revokeSession.isPending}
                    className="flex-shrink-0 px-2.5 py-1.5 rounded-lg bg-red-50 text-red-600 border border-red-100 text-xs font-medium hover:bg-red-100 disabled:opacity-50">
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {typedSessions.length > 1 && (
          <div className="px-5 py-3.5 border-t border-ds-border flex justify-end">
            <button onClick={handleRevokeAll} disabled={revokeAll.isPending}
              className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 border border-red-100 text-xs font-medium hover:bg-red-100 disabled:opacity-50">
              {revokeAll.isPending ? "Signing out..." : "Sign out all other sessions"}
            </button>
          </div>
        )}
      </div>
    </SubPageShell>
  );
}

// ── My feedback — read-only view of the user's own submissions. Status
//    changes and reviewer notes are set by support/admin on the separate
//    review page; this is visibility only, not an editing surface. ──────────

type FeedbackStatus = "open" | "under_review" | "resolved" | "closed";
interface MyFeedbackItem {
  id: string;
  type: "feedback" | "bug";
  title: string;
  status: FeedbackStatus;
  reviewerNote?: string | null;
  createdAt: string;
}

const MY_FEEDBACK_STATUS_STYLE: Record<FeedbackStatus, string> = {
  open: "bg-amber-50 text-amber-700 border-amber-100",
  under_review: "bg-blue-50 text-blue-700 border-blue-100",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-100",
  closed: "bg-gray-50 text-gray-500 border-gray-200",
};
const MY_FEEDBACK_STATUS_LABEL: Record<FeedbackStatus, string> = {
  open: "Open", under_review: "Under review", resolved: "Resolved", closed: "Closed",
};

export function MyFeedbackPage() {
  const { data: items, isLoading } = useMyFeedback();
  const typedItems = (items as MyFeedbackItem[] | undefined) ?? [];

  return (
    <SubPageShell title="My feedback">
      {isLoading && <div className="text-sm text-ds-text-muted">Loading...</div>}

      {!isLoading && typedItems.length === 0 && (
        <div className="bg-white border border-ds-border rounded-card shadow-card p-8 text-center">
          <p className="text-sm text-ds-text-secondary">You haven&apos;t submitted any feedback yet.</p>
          <p className="text-xs text-ds-text-muted mt-1">Use the Feedback button in the bottom-right corner of any page.</p>
        </div>
      )}

      {!isLoading && typedItems.length > 0 && (
        <div className="bg-white border border-ds-border rounded-card shadow-card overflow-hidden divide-y divide-ds-border">
          {typedItems.map((item) => (
            <div key={item.id} className="flex items-start gap-3 px-5 py-3.5">
              <div className={cn(
                "mt-0.5 shrink-0 h-8 w-8 rounded-lg flex items-center justify-center",
                item.type === "bug" ? "bg-red-50 text-red-500" : "bg-blue-50 text-blue-500",
              )}>
                {item.type === "bug" ? <Bug className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ds-text truncate">{item.title}</p>
                <p className="text-xs text-ds-text-muted mt-0.5">
                  Submitted {new Date(item.createdAt).toLocaleDateString()}
                </p>
                {item.reviewerNote && (
                  <p className="text-xs text-ds-text-secondary mt-2 bg-ds-bg rounded-lg px-3 py-2">
                    {item.reviewerNote}
                  </p>
                )}
              </div>
              <span className={cn(
                "shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full border",
                MY_FEEDBACK_STATUS_STYLE[item.status],
              )}>
                {MY_FEEDBACK_STATUS_LABEL[item.status]}
              </span>
            </div>
          ))}
        </div>
      )}
    </SubPageShell>
  );
}

